<template>
  <div>
    <v-col cols="12" class="mt-5 mb-5 d-flex justify-center" v-if="possibleDistributorsPending">
      <v-progress-circular
        :indeterminate="true"
        color="primary"
        size="32"
        width="4"
      />
    </v-col>

    <div
      v-if="!possibleDistributorsPending"
      class="campaign-setup-form pt-3"
    >
      <!--
        PR B1 (Store Editor Redesign Closing Chain) + design completion
        (2026-06-22): the legacy single-card Client & Store section splits
        into two EditorCard wraps:
          - Card 1 "Client company": Company Name + Distributor (SA only)
          - Card 2 "Store details & launch": Store Name + a Pop-Up vs
            Permanent CARD picker + Open/Close dates + timezone chip
            (Pop-Up only) + the Admin/Buyer fields slotted in from
            StoreAddEdit.vue.

        The design-completion pass replaces the intermediate B1
        v-radio-group with a two-card mode picker (matching the
        campaign-editor handoff's mode-first card pattern), adds the
        section intro preamble, and surfaces the timezone chip next to
        the schedule. `storeMode` is still derived from
        `is_permanent_store` in the frontend only -- NO new backend
        column. The eyebrow ("SETUP") + section title ("Client & Store")
        are rendered by the shell (EditorSectionHeader in
        StoreEditShell.vue); this component owns the section body, so the
        lede preamble lives here at the top of the body.

        Each EditorCard owns its own <v-form> so StoreClientContact.vue's
        v-form (rendered via the #client-contact slot inside Card 2) is a
        SIBLING form, not a nested one. Nested <form> elements are
        invalid HTML; browsers self-close the outer form on encountering
        the inner, which would break Vuetify's validation flow. Card 1
        and Card 2 expose their own validation refs (formClient + form);
        validateTab() runs both.
      -->
      <p
        class="client-store-intro"
        data-testid="ct-client-store-intro"
      >
        Tell us who this store is for, when it opens, and who at the client
        we should send reports to. Distributor identity is set by your
        account.
      </p>

      <editor-card title="Client company"
        description="This is the name of the company you are building the store for.">
        <v-form
          ref="formClient"
          v-model="validClient"
        >
          <v-row>
            <v-col cols="12" sm="6" v-if="isSuperAdmin">
              <v-select
                v-model="distributorId"
                :items="possibleDistributors"
                :disabled="
                  disableDistributorList ||
                    warnOnDistributorChange ||
                    $store.getters.getDistributorStatus
                "
                :rules="[validateNotEmpty]"
                :loading="possibleDistributorsPending"
                label="Distributor *"
                browser-autocomplete="new-password"
                item-title="name"
                item-value="_jv.id"
                @update:modelValue="changeDistributor"
                data-testid="ct-name-distributor"
              />
            </v-col>
            <v-col cols="12" sm="6" v-if="isSuperAdmin" />
            <v-col cols="12" sm="6">
              <!-- 2026-08-12 (Jake): "Company Name *" was ambiguous beside the
                   store name; this is the CLIENT's company. Label only -- the
                   required marker and validateNotEmpty rule are unchanged. -->
              <v-text-field
                v-model="companyName"
                label="Client Company Name *"
                :placeholder="defaultCompanyName"
                :rules="[companyNameRule]"
                @update:modelValue="$emit('campaign-changed')"
                data-testid="ct-name-company"
              />
              <!-- Card #499 (Jake, 2026-10-23): the company defaults from the
                   attached client and can be overridden. Say which it is, so
                   two company names on one screen never silently disagree. -->
              <div
                v-if="companyDefaultNote"
                class="text-caption text-grey-darken-1 mt-n4 mb-2"
                data-testid="ct-company-default-note"
              >
                {{ companyDefaultNote }}
              </div>
            </v-col>
          </v-row>
        </v-form>
      </editor-card>

      <editor-card title="Store details &amp; launch"
        description="The store name will appear in the header of the home page.">
        <v-form
          ref="form"
          v-model="validDetails"
        >
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="campaignName"
                label="Store name *"
                :rules="[validateNotEmpty]"
                @update:modelValue="changeCampaignName"
                data-testid="ct-name-campaign"
              />
            </v-col>

            <!--
              Mode-first CARD picker (design completion 2026-06-22):
              "Pop-Up Store" shows the open/close date inputs + timezone
              chip, "Permanent Store" hides them and shows an explainer
              banner. `storeMode` is a computed wrapping `isPermanentStore`
              so we don't need a new column; selecting a card routes
              through the same setter the legacy radio did.
            -->
            <v-col cols="12">
              <div class="store-type-label">Store type</div>
              <div class="store-mode-cards" data-testid="ct-store-mode">
                <button
                  type="button"
                  class="store-mode-card"
                  :class="{ 'store-mode-card--selected': storeMode === 'permanent' }"
                  data-testid="ct-store-mode-permanent"
                  :aria-pressed="storeMode === 'permanent'"
                  @click="selectStoreMode('permanent')"
                >
                  <span class="store-mode-card__icon">
                    <v-icon>all_inclusive</v-icon>
                  </span>
                  <span class="store-mode-card__title">Permanent Store</span>
                  <span class="store-mode-card__sub">
                    Permanent stores stay open indefinitely. Orders are released for purchasing and production as they come in. You can close the store manually at any time.
                  </span>
                </button>
                <button
                  type="button"
                  class="store-mode-card"
                  :class="{ 'store-mode-card--selected': storeMode === 'popup' }"
                  data-testid="ct-store-mode-popup"
                  :aria-pressed="storeMode === 'popup'"
                  @click="selectStoreMode('popup')"
                >
                  <span class="store-mode-card__icon">
                    <v-icon>timer</v-icon>
                  </span>
                  <span class="store-mode-card__title">Pop-Up Store</span>
                  <span class="store-mode-card__sub">
                    Pop-up stores typically have a specific open and close date, which you schedule. With a pop-up store, you can hold all orders for blank goods and decoration until the store closes, then release them to purchasing and production all at once.
                  </span>
                </button>
              </div>
            </v-col>

            <template v-if="storeMode === 'popup'">
              <!--
                `data-value` mirrors the shown date onto the field's root
                element (where Vuetify puts data-* attrs), so UI scenarios can
                assert what the user sees; the input's value is not an
                attribute.
              -->
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="campaignStartDateFormatted"
                  label="Open date *"
                  @update:modelValue="$emit('campaign-changed')"
                  data-testid="ct-name-date-start"
                  :data-value="campaignStartDateFormatted"
                  @click="openRangePicker"
                />
                <div
                  v-if="campaignStartTimeLabel"
                  class="store-window-time"
                  data-testid="ct-name-date-start-time"
                >{{ campaignStartTimeLabel }}</div>
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="campaignEndDateFormatted"
                  label="Close date *"
                  @update:modelValue="$emit('campaign-changed')"
                  data-testid="ct-name-date-end"
                  :data-value="campaignEndDateFormatted"
                  @click="openRangePicker"
                />
                <div
                  v-if="campaignEndTimeLabel"
                  class="store-window-time"
                  data-testid="ct-name-date-end-time"
                >{{ campaignEndTimeLabel }}</div>
              </v-col>
              <v-col cols="12" sm="6" class="d-flex align-end">
                <!--
                  Timezone chip (design completion 2026-06-22). MVP scope:
                  this DISPLAYS the effective timezone the schedule is
                  interpreted in -- the distributor's `timezone` column
                  (reused from the prior distributor-timezone PR), with an
                  Eastern fallback. It is intentionally read-only here: a
                  fully editable per-store timezone needs a new
                  `campaigns.timezone` column, which is backend work out of
                  scope for this frontend pass. Surfacing the effective zone
                  satisfies the design's "show timezone explicitly" intent
                  without inventing a column. See the worknote / PR body.
                -->
                <div
                  class="store-timezone"
                  data-testid="ct-store-timezone-chip"
                >
                  <div class="store-timezone__label">Timezone</div>
                  <div class="store-timezone__chip">
                    <v-icon class="store-timezone__icon">schedule</v-icon>
                    <span>{{ effectiveTimezoneLabel }}</span>
                  </div>
                </div>
              </v-col>
            </template>

            <v-col cols="12" v-else>
              <div
                class="store-mode-permanent-note"
                data-testid="ct-store-mode-permanent-note"
              >
                <v-icon class="store-mode-permanent-note__icon">info</v-icon>
                <span>
                  Permanent stores have no scheduled close. Close manually
                  from this page when ready.
                </span>
              </div>
              <div
                v-if="permanentOpenMoved"
                class="store-mode-permanent-note mt-2"
                data-testid="ct-store-mode-permanent-open-moved"
              >
                <v-icon class="store-mode-permanent-note__icon">info</v-icon>
                <span>
                  Permanent stores open now; your Pop-Up open date was moved
                  to today.
                </span>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </editor-card>

      <!--
        Client contact (design completion 2026-06-22). StoreClientContact.vue
        now owns its own "Client contact" EditorCard, so the slot renders
        as a SIBLING third card (matching the design's three-card stack:
        Client company / Store details & launch / Client contact) rather
        than nested inside Card 2. Still outside any v-form here, so
        StoreClientContact's v-form is a sibling, not a nested HTML <form>.
        Mounted from StoreAddEdit.vue's #section-client-store template.
      -->
      <slot name="client-contact" />
    </div>

    <v-dialog v-model="rangePickerDisplayed" :width="800" persistent>
      <v-card>
        <v-card-title class="text-h5">
          Select Range
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-date-picker
                v-model="tempStartDate"
                label="Start Date"
                data-testid="ct-name-date-range"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-date-picker
                v-model="tempEndDate"
                label="End Date"
              />
            </v-col>
          </v-row>
          <div>
            <v-row class="mt-4">
              <v-col cols="12" lg="6" class="text-center" style="padding: 0 14px">
                <v-text-field
                  type="time"
                  label="Start Time"
                  v-model="campaignStartTime"
                  @update:modelValue="$emit('campaign-changed')"
                  data-testid="ct-name-time-start"
                  hide-details
                />
              </v-col>
              <v-col cols="12" lg="6" class="text-center" style="padding: 0 14px">
                <v-text-field
                  type="time"
                  label="End Time"
                  v-model="campaignEndTime"
                  @update:modelValue="$emit('campaign-changed')"
                  data-testid="ct-name-time-end"
                  hide-details
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            @click="closeRangePicker"
            data-testid="ct-name-range-confirm"
            >Ok</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="warningDialogDisplayed" persistent max-width="290">
      <v-card>
        <v-card-title class="text-h5">Warning!</v-card-title>
        <v-card-text>
          If you change the distributor attached to an existing store, it will
          affect your ability to display and edit products that have already
          been added. Do you still want change distributor?
        </v-card-text>
        <div class="text-center px-2 py-2">
          <div class="flex-grow-1"></div>
          <v-btn color="error" variant="text" @click="warningDialogCancel">Cancel</v-btn>
          <v-btn color="success" variant="text" @click="warningDialogChange"
            >Change</v-btn
          >
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<style>
.vue__time-picker .dropdown {
  top: auto !important;
  bottom: 100% !important;
}
</style>

<script>
import moment from "moment";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import { mapGetters } from "vuex";
import TabError from "./TabError.vue";
import EditorCard from "./components/EditorCard.vue";
import { useAccountStore } from "@/stores/account";
import { COMMON_TIMEZONES } from "@/constants/timezones";
import {
  composeWindowDateTime,
  newPermanentStoreWindow,
  permanentOpenDate,
  popupWindowOnSwitch,
  timeInputValue,
  windowMomentLabel,
  scheduleTimeZone,
  scheduleTimeZoneSource,
} from "./storeWindow.js";

export default {
  // Impersonation fix (2026-06-07, chain item 2 of 3):
  // loadDistributors() now reads `accountStore.distributor.id` (the Pinia
  // *effective* tenant) to lock + pre-select the distributor dropdown.
  // Previously the lock was role-based ("not Super Admin AND own
  // distributor_id non-null"), which silently dropped the SA-impersonating
  // case (role.name stays "Super Admin", own distributor_id is null) and
  // let the SA create a store under ANY tenant -- a P0 tenant-isolation
  // bug. See loadDistributors() for the new dispatch order.
  setup() {
    const accountStore = useAccountStore();
    return { accountStore };
  },

  components: {
    TabError,
    EditorCard,
  },

  props: {
    campaign: Object,
    editMode: Boolean,
  },

  mixins: [helpers, rules],

  data() {
    return {
      // first card
      taxExempt: false,
      lastDistributorId: null,
      distributorId: null,
      possibleDistributors: [],
      possibleDistributorsPending: true,
      disableDistributorList: false,
      campaignName: "",
      companyName: "",
      warnOnDistributorChange: this.$props.editMode,
      warningDialogDisplayed: false,

      // second card
      campaignProducts: [],

      // third card
      // campaignMessage: '',
      // editorConfig: {
      //   toolbar: [
      //     'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'undo', 'redo'
      //   ],
      // },

      // fourth card
      // 24-hour "HH:mm": the only shape a native <input type="time"> takes
      // (card #497). Conversion lives in storeWindow.js.
      campaignStartTime: "00:00",
      campaignEndTime: "00:00",
      // True after a Pop-Up -> Permanent switch moved a future open date to
      // today (PR #4594); drives the hint on the Permanent note.
      permanentOpenMoved: false,
      // A fresh editor is a Permanent store (isPermanentStore below), so its
      // defaults are a Permanent window: open today, no close date.
      campaignStartDate: moment().format("YYYY-MM-DD"),
      campaignEndDate: newPermanentStoreWindow().end_date,
      rangePickerDisplayed: false,
      rangePickerOptions: {
        format: "MM/DD/YYYY",
        startDate:
          this.$props.campaign && this.$props.campaign.start_date
            ? moment(this.$props.campaign.start_date).format("YYYY-MM-DD")
            : null,
        endDate:
          this.$props.campaign && this.$props.campaign.end_date
            ? moment(this.$props.campaign.end_date).format("YYYY-MM-DD")
            : null,
        minDate: moment()
          .subtract(10, "years")
          .format("YYYY-MM-DD"),
        maxDate: moment()
          .add(100, "years")
          .format("YYYY-MM-DD"),
        presets: [
          {
            label: "3 days",
            range: [
              moment().format("YYYY-MM-DD"),
              moment()
                .add(2, "days")
                .format("YYYY-MM-DD"),
            ],
          },
          {
            label: "7 days",
            range: [
              moment().format("YYYY-MM-DD"),
              moment()
                .add(6, "days")
                .format("YYYY-MM-DD"),
            ],
          },
          {
            label: "30 days",
            range: [
              moment().format("YYYY-MM-DD"),
              moment()
                .add(29, "days")
                .format("YYYY-MM-DD"),
            ],
          },
        ],
      },
      // PR B1: two cards == two v-forms. validClient drives Card 1's
      // form, validDetails drives Card 2's form. `valid` is a computed
      // AND of both so the existing parent watcher behavior is
      // unchanged.
      validClient: null,
      validDetails: null,
      isPermanentStore: true,
      // The platform default timezone (GET /platform-settings/default-timezone),
      // the server's fallback when the distributor has no zone. Null until
      // loaded, or when unset.
      platformTimezone: null,
      daterangeKey: 0,      tempStartDate: null,
      tempEndDate: null,    };
  },

  computed: {
    // The linked client's company, offered as the default when the field is
    // blank (resolved server-side; see api/src/services/store_company_name.py).
    defaultCompanyName() {
      return (this.campaign && this.campaign.linked_client_name) || "";
    },
    // Which company the field is using, in words (card #499): the attached
    // client's, by default, or the typed override. Nothing to say without an
    // attached client, or when the typed name is the client's own.
    companyDefaultNote() {
      const client = this.defaultCompanyName.trim();
      if (!client) return "";
      const typed = String(this.companyName || "").trim();
      if (!typed) {
        return `Defaults to ${client}, the attached client's company. Type a name here to use a different one.`;
      }
      if (typed.toLowerCase() === client.toLowerCase()) return "";
      return `Using this name instead of the attached client's company (${client}). Clear it to go back to the client's.`;
    },
    ...mapGetters([
      "isSuperAdmin",
      "isSalesExecutive",
      "isDistributorManager",
    ]),

    selectedDistributor() {
      let matchingDistributors = this.possibleDistributors.filter(
        (obj) => parseInt(obj._jv.id) === parseInt(this.distributorId)
      );
      if (matchingDistributors) {
        return matchingDistributors[0];
      }

      return null;
    },

    campaignStartDateFormatted() {
      return this.$data.campaignStartDate
        ? moment(this.$data.campaignStartDate).format("MM/DD/YYYY")
        : "Not selected";
    },

    // "9:00 AM EDT": the time under the date, in the zone it is read in.
    campaignStartTimeLabel() {
      return windowMomentLabel(
        this.composeDateTime(this.campaignStartDate, this.campaignStartTime),
        this.effectiveTimezone
      );
    },

    campaignEndTimeLabel() {
      return windowMomentLabel(
        this.composeDateTime(this.campaignEndDate, this.campaignEndTime),
        this.effectiveTimezone
      );
    },

    campaignEndDateFormatted() {
      return this.$data.campaignEndDate
        ? moment(this.$data.campaignEndDate).format("MM/DD/YYYY")
        : "Not selected";
    },

    /**
     * PR B1: mode-first picker.
     *
     * `storeMode` is derived from the existing `is_permanent_store` data
     * flag -- no new backend column. Picking "Permanent" sets
     * isPermanentStore=true and the dates hide; "Pop-Up" sets it false
     * and the dates render. The setter also emits campaign-changed so
     * autosave fires the same way the legacy v-switch did.
     *
     * Locked decision (Jake 2026-06-17): NO new column. Frontend-only
     * derivation. See EXECUTION-PLAN-2026-06-16.md, PR B1.
     *
     * This setter is the ONE place the store type changes, so the Pop-Up
     * window rule (Jake 2026-10-01: open no earlier than midnight today in
     * the store's zone, close defaulting to open + 7 days) runs on every
     * switch to Pop-Up and never on a re-click of an already-Pop-Up store.
     */
    storeMode: {
      get() {
        return this.isPermanentStore ? "permanent" : "popup";
      },
      set(value) {
        const next = value === "permanent";
        if (this.isPermanentStore === next) return;
        // Tell the owner when the switch is about to move their open date.
        this.permanentOpenMoved = next && this.openDateWillMoveToToday();
        this.isPermanentStore = next;
        if (next || this.timezoneSource === "distributor") {
          if (!next) this.applyPopupWindowOnSwitch();
          this.$emit("campaign-changed");
          return;
        }
        // "Today" comes from the platform default here, so wait for it
        // rather than compute the window in a zone the server does not use.
        this.platformTimezoneLoad.then(() => {
          if (this.isPermanentStore) return;
          this.applyPopupWindowOnSwitch();
          this.$emit("campaign-changed");
        });
      },
    },

    /**
     * The timezone the Pop-Up schedule is interpreted in, resolved the way
     * the server resolves it (Jake, 2026-10-01): the distributor's
     * `timezone` column (set on the SA distributor-edit page / tenant
     * Profile), else the platform default timezone, else UTC. Never the
     * browser's zone, so the editor and the server agree on "today". The
     * chip shows it and the Pop-Up switch computes "midnight today" in it.
     *
     * MVP scope: display-only. There is no `campaigns.timezone` column,
     * so this is NOT persisted per-store; making the chip editable is a
     * backend follow-up (new column). See PR body / worknote.
     */
    effectiveTimezone() {
      return scheduleTimeZone(
        this.campaign?.distributor?.timezone,
        this.platformTimezone
      );
    },

    timezoneSource() {
      return scheduleTimeZoneSource(
        this.campaign?.distributor?.timezone,
        this.platformTimezone
      );
    },

    effectiveTimezoneLabel() {
      const match = COMMON_TIMEZONES.find(
        (tz) => tz.value === this.effectiveTimezone
      );
      // Fall back to the raw IANA name if it's outside the curated list.
      const name = match ? match.label : this.effectiveTimezone;
      // Say when this is not the distributor's own zone.
      if (this.timezoneSource === "platform") return `${name} (platform default)`;
      if (this.timezoneSource === "utc") return `${name} (no timezone set)`;
      return name;
    },

    /**
     * Aggregate validity across the two card-level v-forms (Card 1 +
     * Card 2). The `valid` watcher and validateTab() both read this
     * single boolean so the rest of the parent <-> child contract is
     * unchanged. Null + null collapses to null (initial, untouched
     * state) to preserve the prior "initial mount does not flag tab
     * invalid" behavior; once any form reports a boolean, the AND
     * applies.
     */
    valid() {
      if (this.validClient === null && this.validDetails === null) {
        return null;
      }
      return (this.validClient !== false) && (this.validDetails !== false);
    },
  },

  created() {
    // Started before any click can switch the store to Pop-Up; the switch
    // waits on it when the distributor has no zone of its own.
    this.platformTimezoneLoad = this.loadPlatformTimezone();
  },

  async mounted() {
    this.$store.state.title = "Store Setup";
    this.$store.state.topTitle = true;

    await this.loadDistributors();
  },

  unmounted() {
    this.$store.state.title = "";
    this.$store.state.topTitle = false;
  },

  watch: {
    campaign: {
      immediate: true,
      handler(newVal) {
        if (this.editMode && !this._hasInitialized && newVal?._jv?.id) {
          this._hasInitialized = true;
          this.fillPreliminaryData();
        }
      },
    },

    distributorId: function(newVal, oldVal) {
      this.lastDistributorId = oldVal;
    },

    valid: function(newVal, oldVal) {
      this.$emit("update-tab-invalid", "campaignSetup", !newVal);
    },
  },

  methods: {
    // Blank is fine while a linked client supplies the company (the server
    // resolves it on read; nothing is copied into the field).
    companyNameRule(v) {
      if (this.defaultCompanyName) return true;
      return this.validateNotEmpty(v);
    },

    validateTab() {
      // Validate both card-level forms (PR B1 split). The legacy single
      // `form` ref maps to Card 2; `formClient` is Card 1.
      if (this.$refs.formClient) {
        this.$refs.formClient.validate();
      }
      if (this.$refs.form) {
        this.$refs.form.validate();
      }
      return !this.valid;
    },

    fillPreliminaryData() {
      // if we are editing a campaign, copy its properties into our component to avoid mutating
      // props. we will save them to the original model when we're done.
      this.$data.distributorId = this.$props.campaign.distributor._jv.id;
      this.$data.campaignName = this.$props.campaign.name;
      if (this.$data.campaignName) {
        this.$store.commit("setEditStoreName", this.$data.campaignName);
      }
      this.$data.companyName = this.$props.campaign.company_name;
      this.$data.campaignStartDate = this.$props.campaign.start_date;
      this.$data.campaignEndDate = this.$props.campaign.end_date;
      this.campaignStartTime = timeInputValue(this.campaignStartDate) || "00:00";
      this.campaignEndTime = timeInputValue(this.campaignEndDate) || "00:00";
      this.taxExempt = this.campaign.tax_exempt;
      this.isPermanentStore = this.campaign.is_permanent_store ? true : false;
    },

    /**
     * Would a Pop-Up -> Permanent switch move the current open date? True
     * for a still-future one (Bug Backlog #329; the rule is
     * permanentOpenDate in storeWindow.js, which the save applies too).
     */
    openDateWillMoveToToday() {
      const open = this.composeDateTime(this.campaignStartDate, this.campaignStartTime);
      if (open === "Invalid date") return false;
      return permanentOpenDate({ open, tz: this.effectiveTimezone }) !== open;
    },

    /**
     * Card-picker click handler (design completion 2026-06-22). Routes
     * through the `storeMode` computed setter so the
     * isPermanentStore-derivation + campaign-changed emit stay in one
     * place. Idempotent: re-clicking the already-selected card is a
     * no-op (the setter guards on a real change).
     */
    selectStoreMode(mode) {
      this.storeMode = mode;
    },

    // Legacy toggle (no template caller today). Routed through the
    // `storeMode` setter so every entry point applies the same Pop-Up rule.
    togglePermanentStore() {
      this.storeMode = this.isPermanentStore ? "popup" : "permanent";
    },

    /**
     * Give a store being switched to Pop-Up a usable window, in the
     * schedule's zone: keep a today-or-later open date (else midnight
     * today), and keep a close date after it (else open + 7 days). The rule
     * itself lives in storeWindow.js;
     * this maps it onto the split date/time fields the inputs bind to.
     */
    applyPopupWindowOnSwitch() {
      const { open, close } = popupWindowOnSwitch({
        open: this.composeDateTime(this.campaignStartDate, this.campaignStartTime),
        close: this.composeDateTime(this.campaignEndDate, this.campaignEndTime),
        tz: this.effectiveTimezone,
      });
      this.campaignStartDate = open.slice(0, 10);
      this.campaignStartTime = timeInputValue(open) || "00:00";
      this.campaignEndDate = close.slice(0, 10);
      this.campaignEndTime = timeInputValue(close) || "00:00";
      this.rangePickerOptions.startDate = this.campaignStartDate;
      this.rangePickerOptions.endDate = this.campaignEndDate;
      this.daterangeKey++;
    },

    /**
     * Join the date field and the time field into the naive
     * "YYYY-MM-DD HH:mm:ss" the API takes. A missing date yields
     * "Invalid date"; a missing time defaults to midnight -- the UI does not
     * mark it required, and an empty time used to crash the backend parser.
     */
    composeDateTime(date, time) {
      return composeWindowDateTime(date, time) || "Invalid date";
    },

    changeCampaignName() {
      this.$store.commit("setEditStoreName", this.$data.campaignName);
      this.$emit("campaign-changed");
    },

    async loadPlatformTimezone() {
      // Best-effort: a failure leaves the UTC fallback, which is also the
      // server's last resort.
      try {
        const resp = await this.$axios.get("/platform-settings/default-timezone");
        const value = resp?.data?.value;
        this.platformTimezone = typeof value === "string" ? value : null;
      } catch (err) {
        this.platformTimezone = null;
      }
    },

    async loadDistributors() {
      this.possibleDistributorsPending = true;
      this.$store.commit("setDistributorLoadPending", true);

      try {
        let result = await this.$axios.get(`/campaign/getDistributorsList`);
        let distributors = result.data;
        let possibleDistributors = Object.values(distributors);

        // Effective-tenant-based lock (impersonation fix, 2026-06-07).
        //
        // Dispatch order:
        //   1. Pinia `accountStore.distributor.id` non-null -- lock the
        //      dropdown to that distributor and pre-select it. Covers:
        //        - Owner viewing own tenant
        //        - SE / DM (their session also carries a distributor)
        //        - SA actively impersonating a tenant
        //      The effective tenant is the only legitimate target for a
        //      store created from this surface.
        //   2. Pinia distributor is null -- true SA, no impersonation, no
        //      own tenant. Show the full distributor list, unlocked, so the
        //      SA can pick any tenant from the admin store-create flow.
        //
        // The previous role-based gate (`role.name != "Super Admin" &&
        // distributor_id != null`) silently dropped the SA-impersonating
        // case (role label doesn't change on impersonation, SA's own
        // distributor_id is null) and let the SA create a store under ANY
        // distributor -- a P0 tenant-isolation bug.
        const piniaDistributorId = this.accountStore?.distributor?.id;
        if (piniaDistributorId != null) {
          possibleDistributors = possibleDistributors.filter(
            (obj) => parseInt(obj._jv.id) === parseInt(piniaDistributorId)
          );
          this.disableDistributorList = true;
          // Coerce to String to match jsonapi-vuex's `_jv.id` type. Pinia
          // stores `distributor.id` as a Number (from `/auth/login` JSON);
          // the v-select uses `item-value="_jv.id"` (String). Strict
          // equality between the v-model (Number) and item-values (String)
          // fails, so the field falls back to rendering the raw value
          // ("1") instead of the matched item's name ("ACME Apparel").
          // Coercing the v-model to String fixes the display lookup. See
          // PR #1372 verify (2026-06-08) for the symptom and task #45.
          this.distributorId = String(piniaDistributorId);
        }

        this.possibleDistributors = possibleDistributors;
      } finally {
        this.possibleDistributorsPending = false;
        this.$store.commit("setDistributorLoadPending", false);
      }
    },

    warningDialogShow() {
      if (!this.disableDistributorList && this.warnOnDistributorChange) {
        this.warningDialogDisplayed = true;
      }
    },

    warningDialogCancel() {
      this.distributorId = this.lastDistributorId;
      this.warningDialogDisplayed = false;
    },

    warningDialogChange() {
      this.warningDialogDisplayed = false;
      this.disableDistributorList = false;
      this.warnOnDistributorChange = false;
    },

    dateRangeChange() {
      // The Start/End Time fields are left alone: Ok used to reset them to
      // midnight, wiping a time the owner had just set (card #497).
      // Convert date picker format to moment format
      this.$data.campaignStartDate = this.tempStartDate ? moment(this.tempStartDate).format("YYYY-MM-DD") : null;
      this.$data.campaignEndDate = this.tempEndDate ? moment(this.tempEndDate).format("YYYY-MM-DD") : null;
    },

    openRangePicker() {
      // Seed the v-date-picker v-models from the campaign's current
      // start/end so the dialog opens with the existing values shown.
      // The underlying string can be bare YYYY-MM-DD or a full ISO
      // datetime like "2026-05-05T00:00:00" (the API serializes it as
      // the latter), so use moment's permissive parser -- matches the
      // strategy `campaignStartDateFormatted` already uses for display.
      // `.startOf("day")` pins the Date to local midnight so the picker
      // doesn't shift a day in some timezones. Null/empty stays null so
      // genuinely-new stores open the picker empty.
      this.tempStartDate = this.campaignStartDate
        ? moment(this.campaignStartDate).startOf("day").toDate()
        : null;
      this.tempEndDate = this.campaignEndDate
        ? moment(this.campaignEndDate).startOf("day").toDate()
        : null;
      this.rangePickerDisplayed = true;
    },

    closeRangePicker() {
      this.dateRangeChange();

      if (!this.$data.campaignStartDate && !this.$data.campaignEndDate) {
        this.$data.campaignStartDate = moment().format("YYYY-MM-DD");
        this.$data.campaignEndDate = moment()
          .add(7, "days")
          .format("YYYY-MM-DD");
      }

      this.rangePickerDisplayed = false;
      this.$emit("campaign-changed");
    },

    updateCampaign(campaign) {
      campaign.error = null;
      if (!this.selectedDistributor && !this.$data.distributorId) {
        campaign.error = "Must select a distributor";
      }

      // A blank store name or client company name is an INCOMPLETE section,
      // not a failed save: the field rules + update-tab-invalid already
      // surface it. Stamping campaign.error here made the parent autosave
      // throw, so a New Store whose company name was still blank dropped
      // every edit -- including the store name the user had just typed.
      // The backend rejects a blank name, so a cleared name field keeps the
      // persisted name instead of sending a blank one.
      const hasName = !!(this.campaignName && this.campaignName.trim());

      let startDate = this.composeDateTime(this.campaignStartDate, this.campaignStartTime);
      let endDate = this.composeDateTime(this.campaignEndDate, this.campaignEndTime);

      // Guard against "Invalid date" from empty/malformed date fields.
      // Open/close dates only exist for Pop-Up stores; Permanent stores
      // have no dates by design (the mode-card picker hides the inputs).
      // So the open-date requirement applies ONLY in Pop-Up mode -- in
      // Permanent mode a missing start_date defaults to "now" so autosave
      // proceeds instead of bailing client-side. Mirrors the close-date
      // handling immediately below.
      if (startDate === "Invalid date" && !this.isPermanentStore) {
        campaign.error = "Open date is required";
        return campaign;
      }
      // Permanent stores get a sensible default open date
      if (startDate === "Invalid date" && this.isPermanentStore) {
        startDate = moment().format("YYYY-MM-DD HH:mm:ss");
      }
      if (endDate === "Invalid date" && !this.isPermanentStore) {
        campaign.error = "Close date is required (or check 'Keep Store Open Indefinitely')";
        return campaign;
      }
      // A Permanent store has no Open date control and a future open date
      // keeps shoppers out, so one left over from the Pop-Up it was is not
      // sent (Bug Backlog #329).
      if (this.isPermanentStore && startDate !== "Invalid date") {
        startDate = permanentOpenDate({
          open: startDate,
          tz: this.effectiveTimezone,
        });
      }
      // A Permanent store stores no close date (Jake, 2026-10-01). One it
      // already has (it was a Pop-Up) is sent back untouched.
      if (endDate === "Invalid date" && this.isPermanentStore) {
        endDate = null;
      }

      // selectedDistributor is the thin /campaign/getDistributorsList row
      // (id, name, stripe id). The parent assigns this result to its
      // campaign, so replacing a loaded distributor with it dropped
      // nexus_states and Tax & Legal read "not set up" (#4291). Overlay the
      // thin row on the loaded one ONLY when it is the same distributor, so
      // a changed distributor never inherits the old one's nexus.
      const loadedDistributor = campaign.distributor;
      const sameDistributor =
        loadedDistributor &&
        this.selectedDistributor &&
        String(loadedDistributor._jv?.id) === String(this.selectedDistributor._jv?.id);
      const data = {
        distributor: sameDistributor
          ? { ...loadedDistributor, ...this.selectedDistributor }
          : this.selectedDistributor,
        distributor_id: this.distributorId,
        company_name: this.companyName,
        name: hasName ? this.campaignName : campaign.name,
        start_date: startDate,
        end_date: endDate,
        tax_exempt: this.taxExempt,
        is_permanent_store: this.isPermanentStore,
      };

      return {
        ...campaign,
        ...data,
      };
    },

    async changeDistributor() {
      this.warningDialogShow();
      this.$emit("campaign-changed");
      // slug-unify Phase 4 PR-3 (#2286): read the canonical ``slug`` (the
      // response no longer carries ``shopper_url``). The existence-check
      // endpoint matches its path segment against ``slug`` on the backend.
      if (
        this.campaign.slug &&
        this.campaign.slug.split(" ").join("")
      ) {
        let shopperUrl = this.campaign.slug
          .split(" ")
          .join("")
          .toLowerCase();
        let campaign_id =
          this.campaign._jv && this.campaign._jv.id ? this.campaign._jv.id : 0;
        let result = await this.$axios.get(
          `/campaign/checkExistingShopperURLByDistributor/${campaign_id}/${this.distributorId}/${shopperUrl}`
        );
        if (result.data.count > 0) {
          this.$store.dispatch(
            "raiseError",
            "Campaign shopper url already exists"
          );
        }
      }
    },
  },
};
</script>

<style scoped>
/*
 * Section intro preamble (lede). The shell renders the eyebrow + title
 * via EditorSectionHeader; this paragraph is the section's lede, placed
 * at the top of the body StoreSetup owns. Tokens per design handoff.
 */
.client-store-intro {
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: var(--tf-fg-3);
  line-height: 1.5;
  max-width: 720px;
  margin: 0 0 var(--tf-sp-5, 20px) 0;
}

/*
 * Store-type CARD picker (replaces the B1 radio group). Two selectable
 * cards matching the campaign-editor "mode-first card picker" pattern.
 */
.store-type-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--tf-fg-4);
  margin-bottom: var(--tf-sp-2, 8px);
}
.store-mode-cards {
  display: flex;
  gap: var(--tf-sp-3, 12px);
  flex-wrap: wrap;
}
.store-mode-card {
  flex: 1 1 220px;
  text-align: left;
  background: var(--tf-surface, #fff);
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius-md, 12px);
  padding: var(--tf-sp-4, 16px) 18px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  display: block;
}
.store-mode-card:hover {
  border-color: var(--tf-primary);
}
.store-mode-card--selected {
  border: 1.5px solid var(--tf-primary);
  box-shadow: 0 0 0 3px var(--tf-primary-soft);
}
.store-mode-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: var(--tf-primary-soft);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--tf-sp-2, 8px);
  color: var(--tf-primary);
}
.store-mode-card--selected .store-mode-card__icon {
  background: var(--tf-primary);
  color: var(--tf-fg-on-primary, #fff);
}
.store-mode-card__title {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--tf-fg-1);
  margin-bottom: 3px;
}
.store-mode-card__sub {
  display: block;
  font-size: 12px;
  color: var(--tf-fg-3);
  line-height: 1.45;
}

/* Permanent-mode explainer banner (replaces the date inputs). */
.store-mode-permanent-note {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-3, 12px);
  background: var(--tf-surface-muted, #fafbfc);
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius-md, 12px);
  padding: 14px 16px;
  font-size: 13px;
  color: var(--tf-fg-3);
}
.store-mode-permanent-note__icon {
  color: var(--tf-fg-4);
  font-size: 18px;
}

/* Open/close time + zone under the date fields (card #497). */
.store-window-time {
  margin: 2px 0 0 16px;
  font-size: 12px;
  opacity: 0.75;
}

/* Timezone chip next to the Pop-Up schedule. */
.store-timezone {
  margin-bottom: var(--tf-sp-2, 8px);
}
.store-timezone__label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--tf-fg-4);
  margin-bottom: var(--tf-sp-1, 4px);
}
.store-timezone__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--tf-surface, #fff);
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius-pill, 9999px);
  padding: 5px 12px;
  font-size: 13px;
  color: var(--tf-fg-1);
}
.store-timezone__icon {
  font-size: 14px;
  color: var(--tf-fg-4);
}

.lock-left {
  left: 0px;
}

.lock-right {
  right: 0px;
}

.time-hider {
  height: 360px;
  width: 115px;
  opacity: 2.7;
  position: absolute;
  z-index: 3;
  background-color: white;
}
</style>

